import React from "react";
import { Tooltip } from "@mui/material";
import type { DataTableColumn } from "../components/EmailDataTableComponent/DataTableComponent";
import type {
  ShipmentApi,
  ShipmentPropertyNames,
} from "../../../src/shipments/types";
import type { ShipmentUI } from "../store/shipmentSlice";
import {
  formatDateToDisplayIgnoreTZ,
  isDateProperty,
} from "@/utils/shipmentDateFormatting";
import { AssigneeCell } from "./AssigneeCell";
import { ReminderCell } from "./ReminderCell";
import type { TableColumnConfig } from "../../../src/shipments/shipmentPropertyDefinitions";

// Helper function to get property values by name from a shipment's properties
const getPropertyValues = (
  shipment: ShipmentUI,
  propertyName: ShipmentPropertyNames,
): string => {
  const matchingProperties = shipment.properties.filter(
    (prop) => prop.name === propertyName,
  );
  if (matchingProperties.length === 0) return "";

  // Get unique values and join with commas
  const uniqueValues = Array.from(
    new Set(matchingProperties.map((prop) => prop.value)),
  );
  return uniqueValues.join(", ");
};

// Helper function to render container numbers with tooltip for overflow
const renderContainerNumbers = (shipment: ShipmentUI) => {
  const matchingProperties = shipment.properties.filter(
    (prop) => prop.name === "container_number",
  );
  if (matchingProperties.length === 0) return "";

  // Get unique values
  const uniqueValues = Array.from(
    new Set(matchingProperties.map((prop) => prop.value)),
  );

  if (uniqueValues.length <= 2) {
    return uniqueValues.join(", ");
  }

  // Show first two and indicate there are more
  const displayText = `${uniqueValues.slice(0, 1).join(", ")} `;
  const moreText = `(${uniqueValues.length - 1} more)`;
  const fullText = uniqueValues.join(", ");

  return (
    <span>
      {displayText}
      <Tooltip title={fullText} arrow>
        <span style={{ textDecoration: "underline", cursor: "pointer" }}>
          {moreText}
        </span>
      </Tooltip>
    </span>
  );
};

/**
 * Build table columns from config-driven column definitions.
 */
export function buildColumnsFromConfig(
  columns: TableColumnConfig[],
): DataTableColumn<ShipmentUI>[] {
  return columns.map((col): DataTableColumn<ShipmentUI> => {
    switch (col.type) {
      case "assignee":
        return {
          columnName: col.name,
          renderFn: (shipment) => (
            <AssigneeCell
              shipmentId={shipment.id}
              assignedToIds={shipment.assignedToIds}
              isTemporary={shipment.isTemporary}
            />
          ),
          clipText: false,
          flex: col.flex,
          visibleByDefault: col.visibleByDefault,
          editable: col.editable,
        };

      case "exception":
        return {
          columnName: col.name,
          renderFn: (shipment) => (
            <ReminderCell
              shipmentId={shipment.id}
              unresolvedExceptions={shipment.unresolvedExceptions}
              unresolvedMilestones={shipment.unresolvedMilestones}
            />
          ),
          clipText: false,
          flex: col.flex,
          visibleByDefault: col.visibleByDefault,
          editable: col.editable ?? false,
        };

      case "reminder":
        return {
          columnName: col.name,
          renderFn: (shipment) => (
            <ReminderCell
              shipmentId={shipment.id}
              unresolvedExceptions={shipment.unresolvedExceptions}
              unresolvedMilestones={shipment.unresolvedMilestones}
            />
          ),
          clipText: false,
          flex: col.flex,
          visibleByDefault: col.visibleByDefault,
          editable: col.editable ?? false,
        };

      case "property": {
        const propName = col.property;
        if (!propName) {
          return {
            columnName: col.name,
            renderFn: () => "",
            clipText: true,
            flex: col.flex,
            visibleByDefault: col.visibleByDefault,
          };
        }

        if (propName === "container_number") {
          return {
            columnName: col.name,
            fieldName: propName as any,
            renderFn: renderContainerNumbers,
            valueFn: (shipment) => getPropertyValues(shipment, propName),
            clipText: false,
            width: 200,
            flex: col.flex,
            visibleByDefault: col.visibleByDefault,
            editable: col.editable,
          };
        }

        if (isDateProperty(propName)) {
          return {
            columnName: col.name,
            fieldName: propName as any,
            renderFn: (shipment) => {
              const val = getPropertyValues(shipment, propName);
              if (!val) return "-";
              return formatDateToDisplayIgnoreTZ(val);
            },
            valueFn: (shipment) => getPropertyValues(shipment, propName),
            clipText: true,
            flex: col.flex,
            visibleByDefault: col.visibleByDefault,
            editable: col.editable,
          };
        }

        return {
          columnName: col.name,
          fieldName: propName as any,
          renderFn: (shipment) => getPropertyValues(shipment, propName),
          clipText: true,
          flex: col.flex,
          visibleByDefault: col.visibleByDefault,
          editable: col.editable,
        };
      }

      default:
        return {
          columnName: col.name,
          renderFn: () => "",
          clipText: true,
          flex: col.flex,
          visibleByDefault: col.visibleByDefault,
        };
    }
  });
}
